import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import { hostApi, Host } from '../../services/api/host.api';
import { containerApi, Container } from '../../services/api/container.api';

export default function InfrastructurePage() {
  const { t } = useTranslation(['infrastructure', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hosts, setHosts] = useState<Host[]>([]);
  const [containers, setContainers] = useState<Container[]>([]);
  const [hostsPage, setHostsPage] = useState(1);
  const [hostsPageSize, setHostsPageSize] = useState(20);
  const [totalHosts, setTotalHosts] = useState(0);
  const [containersPage, setContainersPage] = useState(1);
  const [containersPageSize, setContainersPageSize] = useState(20);
  const [totalContainers, setTotalContainers] = useState(0);

  useEffect(() => {
    fetchData();
  }, [hostsPage, hostsPageSize, containersPage, containersPageSize]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const hostsOffset = (hostsPage - 1) * hostsPageSize;
      const containersOffset = (containersPage - 1) * containersPageSize;

      const [hostsResult, containersResult] = await Promise.all([
        hostApi.getHosts(hostsPageSize, hostsOffset),
        containerApi.getContainers(containersPageSize, containersOffset)
      ]);

      setHosts(hostsResult.data);
      setTotalHosts(hostsResult.meta.total);
      setContainers(containersResult.data);
      setTotalContainers(containersResult.meta.total);
    } catch (err: any) {
      setError(err.message || '{t("common:error")}');
      console.error('Infrastructure fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
      case 'healthy':
        return 'bg-green-500';
      case 'degraded':
      case 'warning':
        return 'bg-yellow-500';
      case 'offline':
      case 'critical':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getProgressColor = (value: number) => {
    if (value > 80) return 'bg-red-500';
    if (value > 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">{t("common:loading")}</div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('infrastructure:title')}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('infrastructure:subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t("infrastructure:stats.totalHosts")}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{totalHosts}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t("infrastructure:stats.onlineHosts")}</p>
          <p className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">
            {hosts.filter(h => h.status === 'online').length}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t("infrastructure:stats.totalContainers")}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{totalContainers}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t("infrastructure:stats.runningContainers")}</p>
          <p className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">
            {containers.filter(c => c.status === 'online').length}
          </p>
        </div>
      </div>

      {/* Hosts */}
      <div className="mb-8">
        <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">{t('infrastructure:hosts.title')}</h2>
        {hosts.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400">
            {t('infrastructure:hosts.noHostsFound')}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {hosts.map((host) => (
              <div key={host.id} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{host.hostname}</h3>
                  <span className={`h-3 w-3 rounded-full ${getStatusColor(host.status)}`}></span>
                </div>
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">{host.ip_address}</p>
                <div className="space-y-3">
                  {host.metadata?.cpu_usage !== undefined && (
                    <div>
                      <div className="mb-1 flex justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-400">{t('infrastructure:hosts.cpu')}</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{host.metadata.cpu_usage.toFixed(0)}%</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className={`h-2 rounded-full ${getProgressColor(host.metadata.cpu_usage)}`}
                          style={{ width: `${Math.min(100, host.metadata.cpu_usage)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  {host.metadata?.memory_usage !== undefined && (
                    <div>
                      <div className="mb-1 flex justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-400">{t('infrastructure:hosts.memory')}</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{host.metadata.memory_usage.toFixed(0)}%</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className={`h-2 rounded-full ${getProgressColor(host.metadata.memory_usage)}`}
                          style={{ width: `${Math.min(100, host.metadata.memory_usage)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  {host.metadata?.disk_usage !== undefined && (
                    <div>
                      <div className="mb-1 flex justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-400">{t('infrastructure:hosts.disk')}</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{host.metadata.disk_usage.toFixed(0)}%</span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className={`h-2 rounded-full ${getProgressColor(host.metadata.disk_usage)}`}
                          style={{ width: `${Math.min(100, host.metadata.disk_usage)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Hosts Pagination */}
        {hosts.length > 0 && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('infrastructure:pagination.page')} {hostsPage} {t('infrastructure:pagination.of')} {Math.ceil(totalHosts / hostsPageSize)} ({totalHosts} {t('infrastructure:pagination.totalHosts')})
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => setHostsPage(hostsPage - 1)}
                disabled={hostsPage === 1}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('infrastructure:pagination.previous')}
              </button>
              <button
                onClick={() => setHostsPage(hostsPage + 1)}
                disabled={hostsPage >= Math.ceil(totalHosts / hostsPageSize)}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('infrastructure:pagination.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Containers */}
      <div>
        <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">{t('infrastructure:containers.title')}</h2>
        {containers.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400">
            {t('infrastructure:containers.noContainersFound')}
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <table className="w-full">
              <thead className="border-b border-gray-200 dark:border-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('infrastructure:containers.name')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('infrastructure:containers.image')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('infrastructure:containers.status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{t('infrastructure:containers.hostname')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {containers.map((container) => (
                  <tr key={container.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900 dark:text-white">{container.name}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {container.tags?.image || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                        container.status === 'online'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                          : container.status === 'degraded'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                      }`}>
                        {container.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {container.hostname || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Containers Pagination */}
        {containers.length > 0 && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('infrastructure:pagination.page')} {containersPage} {t('infrastructure:pagination.of')} {Math.ceil(totalContainers / containersPageSize)} ({totalContainers} {t('infrastructure:pagination.totalContainers')})
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => setContainersPage(containersPage - 1)}
                disabled={containersPage === 1}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('infrastructure:pagination.previous')}
              </button>
              <button
                onClick={() => setContainersPage(containersPage + 1)}
                disabled={containersPage >= Math.ceil(totalContainers / containersPageSize)}
                className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('infrastructure:pagination.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
